import { useEffect, useMemo, useState } from "react";
import {
  BookOpenCheck,
  ContactRound,
  Download,
  FileText,
  Landmark,
  Lock,
  Plus,
  RefreshCw,
  RotateCcw,
  Scale,
} from "lucide-react";
import { toast } from "sonner";

import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Breadcrumbs,
  PageHeader,
  StatCard,
  WorkflowGuide,
  inr,
  Loading,
  useTabParam,
} from "../../components/shared";
import { useSocietyFeatures } from "../../context/SocietyFeatureContext";

const ACCOUNTING_TABS = ["books", "payables", "reports", "setup"];

const today = () => new Date().toISOString().slice(0, 10);

export default function SocietyAccounting() {
  const [bootstrap, setBootstrap] = useState(null);
  const [charges, setCharges] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [reports, setReports] = useState({
    trial: null,
    income: null,
    balance: null,
    reconciliation: null,
  });

  const load = async () => {
    try {
      const [
        book,
        chargeRows,
        mappingRows,
        exceptionRows,
        voucherRows,
        supplierRows,
        trial,
        income,
        balance,
        reconciliation,
      ] = await Promise.all([
        api.get("/society-accounting/bootstrap"),
        api.get("/society-finance/charges"),
        api.get("/society-accounting/mappings"),
        api.get("/society-accounting/exceptions"),
        api.get("/society-accounting/vouchers"),
        api.get("/society-accounting/suppliers"),
        api.get("/society-accounting/reports/trial-balance"),
        api.get("/society-accounting/reports/income-expenditure"),
        api.get("/society-accounting/reports/balance-sheet"),
        api.get("/society-accounting/reports/reconciliation"),
      ]);
      setBootstrap(book.data);
      setCharges(chargeRows.data);
      setMappings(mappingRows.data);
      setExceptions(exceptionRows.data);
      setVouchers(voucherRows.data);
      setSuppliers(supplierRows.data);
      setReports({
        trial: trial.data,
        income: income.data,
        balance: balance.data,
        reconciliation: reconciliation.data,
      });
      const purchaseResponse = await api.get("/society-accounting/purchases");
      setPurchases(purchaseResponse.data);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not load Society Accounting",
      );
    }
  };

  useEffect(() => {
    load();
  }, []);

  const accounts = bootstrap?.accounts || [];
  const settings = bootstrap?.settings || {};
  const activeVouchers = vouchers.filter((voucher) => !voucher.reversal_of);
  const { byKey } = useSocietyFeatures();
  const featureOn = (key) => Boolean(byKey?.(key)?.allowed);
  const [tabParam, setAcctTab] = useTabParam("books");
  const acctTab = ACCOUNTING_TABS.includes(tabParam) ? tabParam : "books";

  if (!bootstrap || !reports.trial) {
    return <Loading />;
  }

  return (
    <div data-testid="society-accounting-page">
      <Breadcrumbs
        items={[
          { label: "Dashboard", to: "/app" },
          { label: "Finance & Accounting" },
          { label: "Society Accounting" },
        ]}
      />
      <PageHeader
        title="Society Accounting"
        subtitle="Immutable double-entry books, distinct from maintenance billing and collections."
      >
        <Button
          data-testid="accounting-refresh-button"
          onClick={load}
          variant="outline"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh books
        </Button>
      </PageHeader>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Scale}
          label="Trial Balance"
          testid="accounting-trial-balance-kpi"
          tone={
            reports.trial.total_debit === reports.trial.total_credit
              ? "secondary"
              : "rose"
          }
          value={`${inr(reports.trial.total_debit)} / ${inr(reports.trial.total_credit)}`}
        />
        <StatCard
          icon={BookOpenCheck}
          label="Posted vouchers"
          testid="accounting-voucher-count-kpi"
          value={String(vouchers.length)}
        />
        <StatCard
          icon={Landmark}
          label="Member receivable"
          testid="accounting-receivable-kpi"
          tone="amber"
          value={inr(reports.reconciliation.sm04_member_receivable)}
        />
        <StatCard
          icon={FileText}
          label="SM-03 reconciliation"
          testid="accounting-reconciliation-kpi"
          tone={reports.reconciliation.status === "PASS" ? "secondary" : "rose"}
          value={reports.reconciliation.status}
        />
      </section>

      <Tabs className="mt-8" onValueChange={setAcctTab} value={acctTab}>
        <TabsList className="h-auto flex-wrap" data-testid="accounting-tabs">
          <TabsTrigger data-testid="accounting-books-tab" value="books">
            Books & vouchers
          </TabsTrigger>
          <TabsTrigger data-testid="accounting-payables-tab" value="payables">
            Purchases & payables
          </TabsTrigger>
          <TabsTrigger data-testid="accounting-reports-tab" value="reports">
            Statements & reports
          </TabsTrigger>
          <TabsTrigger data-testid="accounting-setup-tab" value="setup">
            Setup & mappings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="books">
          <BooksPanel
            accounts={accounts}
            onReload={load}
            vouchers={activeVouchers}
          />
        </TabsContent>
        <TabsContent value="payables">
          <PayablesPanel
            accounts={accounts}
            onReload={load}
            purchases={purchases}
            suppliers={suppliers}
          />
        </TabsContent>
        <TabsContent value="reports">
          <ReportsPanel reports={reports} />
        </TabsContent>
        <TabsContent value="setup">
          <SetupPanel
            accounts={accounts}
            charges={charges}
            exceptions={exceptions}
            mappings={mappings}
            onReload={load}
            settings={settings}
          />
        </TabsContent>
      </Tabs>

      <WorkflowGuide
        steps={[
          {
            label: "Maintenance Finance",
            to: "/app/finance",
            icon: Landmark,
            testid: "accounting-guide-finance",
            show: featureOn("sm03_billing"),
          },
          {
            label: "Members & Units",
            to: "/app/directory",
            icon: ContactRound,
            testid: "accounting-guide-directory",
            show: featureOn("sm02_directory"),
          },
          {
            label: "Finance Report centre",
            to: "/app/finance?tab=p3-reports",
            icon: FileText,
            testid: "accounting-guide-reports",
            show: featureOn("sm03_billing"),
          },
        ]}
        title="Related finance workflows"
      />
    </div>
  );
}

function BooksPanel({ accounts, onReload, vouchers }) {
  const [journal, setJournal] = useState({
    credit_account_id: "",
    date: today(),
    debit_account_id: "",
    narration: "",
    amount: "",
  });
  const [contra, setContra] = useState({
    amount: "",
    date: today(),
    direction: "CASH_TO_BANK",
  });
  const saveJournal = async () => {
    try {
      await api.post("/society-accounting/vouchers/journal", {
        date: journal.date,
        narration: journal.narration,
        lines: [
          {
            account_id: journal.debit_account_id,
            debit: journal.amount,
            credit: 0,
          },
          {
            account_id: journal.credit_account_id,
            debit: 0,
            credit: journal.amount,
          },
        ],
      });
      toast.success("Balanced journal posted");
      setJournal({
        credit_account_id: "",
        date: today(),
        debit_account_id: "",
        narration: "",
        amount: "",
      });
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not post journal");
    }
  };
  const saveContra = async () => {
    try {
      await api.post("/society-accounting/contra", contra);
      toast.success("Contra voucher posted");
      setContra({ amount: "", date: today(), direction: "CASH_TO_BANK" });
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not post contra");
    }
  };
  const reverse = async (voucher) => {
    const reason = window.prompt("Reason for controlled reversal");
    if (!reason?.trim()) return;
    try {
      await api.post(
        `/society-accounting/vouchers/${voucher.voucher_id}/reverse`,
        { reason },
      );
      toast.success("Reversal voucher posted; original remains immutable");
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not reverse voucher");
    }
  };
  return (
    <section className="mt-5 space-y-8" data-testid="accounting-books-panel">
      <div className="grid gap-6 xl:grid-cols-2">
        <form
          className="border bg-card p-5 shadow-sm"
          data-testid="accounting-journal-form"
          onSubmit={(event) => {
            event.preventDefault();
            saveJournal();
          }}
        >
          <h2 className="font-display text-xl font-bold">Manual journal</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            The server accepts only exact debit-credit equality.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Input
              data-testid="accounting-journal-date"
              onChange={(event) =>
                setJournal({ ...journal, date: event.target.value })
              }
              type="date"
              value={journal.date}
            />
            <Input
              data-testid="accounting-journal-amount"
              min="0.01"
              onChange={(event) =>
                setJournal({ ...journal, amount: event.target.value })
              }
              placeholder="Amount"
              required
              type="number"
              value={journal.amount}
            />
            <AccountSelect
              accounts={accounts}
              id="accounting-journal-debit-account"
              label="Debit account"
              onChange={(value) =>
                setJournal({ ...journal, debit_account_id: value })
              }
              value={journal.debit_account_id}
            />
            <AccountSelect
              accounts={accounts}
              id="accounting-journal-credit-account"
              label="Credit account"
              onChange={(value) =>
                setJournal({ ...journal, credit_account_id: value })
              }
              value={journal.credit_account_id}
            />
            <Input
              className="sm:col-span-2"
              data-testid="accounting-journal-narration"
              onChange={(event) =>
                setJournal({ ...journal, narration: event.target.value })
              }
              placeholder="Narration"
              required
              value={journal.narration}
            />
          </div>
          <Button
            className="mt-4"
            data-testid="accounting-post-journal-button"
            type="submit"
          >
            <Plus className="h-4 w-4" />
            Post journal
          </Button>
        </form>
        <form
          className="border bg-card p-5 shadow-sm"
          data-testid="accounting-contra-form"
          onSubmit={(event) => {
            event.preventDefault();
            saveContra();
          }}
        >
          <h2 className="font-display text-xl font-bold">Cash / bank contra</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Moves cash and bank only; never income or expense.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Input
              data-testid="accounting-contra-date"
              onChange={(event) =>
                setContra({ ...contra, date: event.target.value })
              }
              type="date"
              value={contra.date}
            />
            <Input
              data-testid="accounting-contra-amount"
              min="0.01"
              onChange={(event) =>
                setContra({ ...contra, amount: event.target.value })
              }
              placeholder="Amount"
              required
              type="number"
              value={contra.amount}
            />
            <select
              className="border bg-background px-3 py-2 text-sm sm:col-span-2"
              data-testid="accounting-contra-direction"
              onChange={(event) =>
                setContra({ ...contra, direction: event.target.value })
              }
              value={contra.direction}
            >
              <option value="CASH_TO_BANK">Cash to bank</option>
              <option value="BANK_TO_CASH">Bank to cash</option>
            </select>
          </div>
          <Button
            className="mt-4"
            data-testid="accounting-post-contra-button"
            type="submit"
          >
            <Landmark className="h-4 w-4" />
            Post contra
          </Button>
        </form>
      </div>
      <VoucherTable onReverse={reverse} vouchers={vouchers} />
    </section>
  );
}

function PayablesPanel({ accounts, onReload, purchases, suppliers }) {
  const expenses = accounts.filter((account) => account.type === "expense");
  const [supplier, setSupplier] = useState({ email: "", mobile: "", name: "" });
  const [purchase, setPurchase] = useState({
    amount: "",
    date: today(),
    expense_account_id: "",
    invoice_number: "",
    supplier_id: "",
  });
  const [payment, setPayment] = useState({
    amount: "",
    date: today(),
    mode: "Bank",
    purchase_id: "",
    reference: "",
  });
  const saveSupplier = async () => {
    try {
      await api.post("/society-accounting/suppliers", supplier);
      toast.success("Supplier saved");
      setSupplier({ email: "", mobile: "", name: "" });
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save supplier");
    }
  };
  const savePurchase = async () => {
    try {
      await api.post("/society-accounting/purchases", purchase);
      toast.success("Expense and payable voucher posted");
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not post purchase");
    }
  };
  const savePayment = async () => {
    try {
      await api.post("/society-accounting/payments", payment);
      toast.success("Supplier payment posted");
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not post payment");
    }
  };
  return (
    <section className="mt-5 space-y-8" data-testid="accounting-payables-panel">
      <div className="grid gap-6 xl:grid-cols-3">
        <CompactForm
          title="Add supplier"
          testid="accounting-supplier-form"
          onSubmit={saveSupplier}
        >
          <Input
            data-testid="accounting-supplier-name"
            onChange={(event) =>
              setSupplier({ ...supplier, name: event.target.value })
            }
            placeholder="Supplier name"
            required
            value={supplier.name}
          />
          <Input
            data-testid="accounting-supplier-mobile"
            onChange={(event) =>
              setSupplier({ ...supplier, mobile: event.target.value })
            }
            placeholder="Mobile"
            value={supplier.mobile}
          />
          <Input
            data-testid="accounting-supplier-email"
            onChange={(event) =>
              setSupplier({ ...supplier, email: event.target.value })
            }
            placeholder="Email"
            type="email"
            value={supplier.email}
          />
          <Button data-testid="accounting-save-supplier-button" type="submit">
            Save supplier
          </Button>
        </CompactForm>
        <CompactForm
          title="Record purchase / expense"
          testid="accounting-purchase-form"
          onSubmit={savePurchase}
        >
          <BasicSelect
            id="accounting-purchase-supplier"
            items={suppliers}
            label="Supplier"
            onChange={(value) =>
              setPurchase({ ...purchase, supplier_id: value })
            }
            value={purchase.supplier_id}
          />
          <AccountSelect
            accounts={expenses}
            id="accounting-purchase-expense-account"
            label="Expense account"
            onChange={(value) =>
              setPurchase({ ...purchase, expense_account_id: value })
            }
            value={purchase.expense_account_id}
          />
          <Input
            data-testid="accounting-purchase-amount"
            min="0.01"
            onChange={(event) =>
              setPurchase({ ...purchase, amount: event.target.value })
            }
            placeholder="Amount"
            required
            type="number"
            value={purchase.amount}
          />
          <Input
            data-testid="accounting-purchase-invoice"
            onChange={(event) =>
              setPurchase({ ...purchase, invoice_number: event.target.value })
            }
            placeholder="Vendor invoice"
            value={purchase.invoice_number}
          />
          <Input
            data-testid="accounting-purchase-date"
            onChange={(event) =>
              setPurchase({ ...purchase, date: event.target.value })
            }
            type="date"
            value={purchase.date}
          />
          <Button data-testid="accounting-save-purchase-button" type="submit">
            Post payable
          </Button>
        </CompactForm>
        <CompactForm
          title="Pay supplier"
          testid="accounting-payment-form"
          onSubmit={savePayment}
        >
          <BasicSelect
            id="accounting-payment-purchase"
            items={purchases.filter((item) => item.status === "OPEN")}
            label="Open purchase"
            onChange={(value) => setPayment({ ...payment, purchase_id: value })}
            value={payment.purchase_id}
          />
          <Input
            data-testid="accounting-payment-amount"
            min="0.01"
            onChange={(event) =>
              setPayment({ ...payment, amount: event.target.value })
            }
            placeholder="Amount"
            required
            type="number"
            value={payment.amount}
          />
          <select
            className="border bg-background px-3 py-2 text-sm"
            data-testid="accounting-payment-mode"
            onChange={(event) =>
              setPayment({ ...payment, mode: event.target.value })
            }
            value={payment.mode}
          >
            <option>Bank</option>
            <option>Cash</option>
          </select>
          <Input
            data-testid="accounting-payment-reference"
            onChange={(event) =>
              setPayment({ ...payment, reference: event.target.value })
            }
            placeholder="Reference"
            value={payment.reference}
          />
          <Input
            data-testid="accounting-payment-date"
            onChange={(event) =>
              setPayment({ ...payment, date: event.target.value })
            }
            type="date"
            value={payment.date}
          />
          <Button data-testid="accounting-save-payment-button" type="submit">
            Post payment
          </Button>
        </CompactForm>
      </div>
      <div
        className="overflow-x-auto border bg-card"
        data-testid="accounting-purchases-table"
      >
        <table className="min-w-[760px] w-full text-sm">
          <thead className="bg-muted/60">
            <tr>
              <th className="p-3 text-left">Supplier</th>
              <th className="p-3 text-left">Invoice</th>
              <th className="p-3 text-right">Amount</th>
              <th className="p-3 text-right">Payable</th>
              <th className="p-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {purchases.length === 0 ? (
              <tr>
                <td
                  className="p-8 text-center text-muted-foreground"
                  colSpan={5}
                >
                  No purchases recorded.
                </td>
              </tr>
            ) : (
              purchases.map((item) => (
                <tr
                  className="border-t"
                  data-testid={`accounting-purchase-${item.purchase_id}`}
                  key={item.purchase_id}
                >
                  <td className="p-3">{item.supplier_name}</td>
                  <td className="p-3">{item.invoice_number || "—"}</td>
                  <td className="p-3 text-right">{inr(item.amount)}</td>
                  <td className="p-3 text-right">{inr(item.outstanding)}</td>
                  <td className="p-3">{item.status}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ReportsPanel({ reports }) {
  const formats = ["trial-balance", "income-expenditure", "balance-sheet"];
  return (
    <section className="mt-5 space-y-6" data-testid="accounting-reports-panel">
      <div className="flex flex-wrap gap-2">
        {formats.map((report) => (
          <span className="inline-flex gap-1" key={report}>
            <a
              data-testid={`accounting-${report}-csv`}
              href={`${API}/society-accounting/reports/${report}.csv`}
            >
              <Button size="sm" variant="outline">
                <Download className="h-4 w-4" />
                {report} CSV
              </Button>
            </a>
            <Button
              data-testid={`accounting-${report}-pdf`}
              onClick={() =>
                window.open(
                  `${API}/society-accounting/reports/${report}.pdf`,
                  "_blank",
                )
              }
              size="sm"
              variant="outline"
            >
              PDF
            </Button>
          </span>
        ))}
      </div>
      <ReportTable
        title="Trial Balance"
        rows={reports.trial.rows}
        totals={[reports.trial.total_debit, reports.trial.total_credit]}
      />
      <div className="grid gap-6 xl:grid-cols-2">
        <Statement
          title="Income & Expenditure"
          left={reports.income.income}
          leftTitle="Income"
          right={reports.income.expenses}
          rightTitle="Expenditure"
          footer={`Surplus: ${inr(reports.income.surplus)}`}
        />
        <Statement
          title="Balance Sheet"
          left={reports.balance.assets}
          leftTitle="Assets"
          right={reports.balance.equity_liabilities}
          rightTitle="Funds & liabilities"
          footer={
            `${inr(reports.balance.total_assets)} = ${inr(
              reports.balance.total_equity_liabilities,
            )}`
          }
        />
      </div>
      <div
        className="border bg-card p-5"
        data-testid="accounting-reconciliation-report"
      >
        <h2 className="font-display text-xl font-bold">
          SM-03 ↔ SM-04 reconciliation
        </h2>
        <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
          <Metric
            label="SM-03 outstanding"
            value={inr(reports.reconciliation.sm03_outstanding)}
          />
          <Metric
            label="SM-04 member receivable"
            value={inr(reports.reconciliation.sm04_member_receivable)}
          />
          <Metric
            label="Difference"
            value={inr(reports.reconciliation.difference)}
          />
        </div>
        <p
          className="mt-4 font-semibold"
          data-testid="accounting-reconciliation-status"
        >
          Status: {reports.reconciliation.status} · Receipts{" "}
          {reports.reconciliation.receipt_postings}/
          {reports.reconciliation.receipts_finalized} posted
        </p>
      </div>
    </section>
  );
}

function SetupPanel({
  accounts,
  charges,
  exceptions,
  mappings,
  onReload,
  settings,
}) {
  const [form, setForm] = useState({
    cutover_date: settings.cutover_date || "",
    financial_year_start: settings.financial_year_start || "04-01",
    status: settings.status || "DRAFT",
  });
  const mapByCharge = useMemo(
    () =>
      new Map(
        mappings
          .filter((item) => item.source_type === "SM03_CHARGE")
          .map((item) => [item.source_key, item.account_id]),
      ),
    [mappings],
  );
  const saveSettings = async () => {
    try {
      await api.put("/society-accounting/settings", form);
      toast.success("Accounting settings saved");
      onReload();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not save accounting settings",
      );
    }
  };
  const saveMapping = async (chargeId, accountId) => {
    try {
      await api.post("/society-accounting/mappings", {
        source_type: "SM03_CHARGE",
        source_key: chargeId,
        account_id: accountId,
      });
      toast.success("Charge mapping saved");
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save mapping");
    }
  };
  const retry = async (exception) => {
    try {
      await api.post(
        `/society-accounting/exceptions/${exception.source_type}/${exception.source_id}/retry`,
      );
      toast.success("Accounting source retried");
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not retry source");
    }
  };
  const lock = async () => {
    const period = window.prompt("Period to lock (YYYY-MM)");
    if (!period) return;
    try {
      await api.post(`/society-accounting/periods/${period}/lock`);
      toast.success("Period locked");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not lock period");
    }
  };
  return (
    <section className="mt-5 space-y-8" data-testid="accounting-setup-panel">
      <div className="grid gap-6 xl:grid-cols-2">
        <form
          className="border bg-card p-5"
          data-testid="accounting-settings-form"
          onSubmit={(event) => {
            event.preventDefault();
            saveSettings();
          }}
        >
          <h2 className="font-display text-xl font-bold">
            Financial year and cutover
          </h2>
          <div className="mt-4 grid gap-3">
            <Input
              data-testid="accounting-financial-year-start"
              onChange={(event) =>
                setForm({ ...form, financial_year_start: event.target.value })
              }
              placeholder="MM-DD"
              value={form.financial_year_start}
            />
            <Input
              data-testid="accounting-cutover-date"
              onChange={(event) =>
                setForm({ ...form, cutover_date: event.target.value })
              }
              required
              type="date"
              value={form.cutover_date}
            />
            <select
              className="border bg-background px-3 py-2 text-sm"
              data-testid="accounting-status-select"
              onChange={(event) =>
                setForm({ ...form, status: event.target.value })
              }
              value={form.status}
            >
              <option value="DRAFT">Draft</option>
              <option value="ACTIVE">Active</option>
            </select>
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="accounting-save-settings-button"
                type="submit"
              >
                Save accounting setup
              </Button>
              <Button
                data-testid="accounting-lock-period-button"
                onClick={lock}
                type="button"
                variant="outline"
              >
                <Lock className="h-4 w-4" />
                Lock period
              </Button>
            </div>
          </div>
        </form>
        <div className="border bg-card p-5">
          <h2 className="font-display text-xl font-bold">
            Integration exceptions
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Unmapped sources remain pending; no guessed or partial voucher is
            created.
          </p>
          <div
            className="mt-4 space-y-2"
            data-testid="accounting-exceptions-list"
          >
            {exceptions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No pending exceptions.
              </p>
            ) : (
              exceptions.map((item) => (
                <div
                  className="flex flex-wrap items-center justify-between gap-2 border p-3 text-sm"
                  data-testid={`accounting-exception-${item.exception_id}`}
                  key={item.exception_id}
                >
                  <span>{item.detail}</span>
                  <Button
                    data-testid={`accounting-retry-exception-${item.exception_id}`}
                    onClick={() => retry(item)}
                    size="sm"
                    variant="outline"
                  >
                    Retry
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <div className="border bg-card p-5">
        <h2 className="font-display text-xl font-bold">
          SM-03 charge mappings
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Map each finalized operational charge to its Society ledger before
          accounting can post it.
        </p>
        <div className="mt-5 grid gap-3 lg:grid-cols-2">
          {charges.map((charge) => (
            <label
              className="grid gap-1 border p-3 text-sm"
              data-testid={`accounting-charge-mapping-${charge.charge_id}`}
              key={charge.charge_id}
            >
              <span className="font-medium">{charge.name}</span>
              <select
                data-testid={`accounting-charge-map-select-${charge.charge_id}`}
                onChange={(event) =>
                  saveMapping(charge.charge_id, event.target.value)
                }
                value={mapByCharge.get(charge.charge_id) || ""}
              >
                <option value="">Select ledger</option>
                {accounts
                  .filter((account) =>
                    ["income", "liability"].includes(account.type),
                  )
                  .map((account) => (
                    <option key={account.account_id} value={account.account_id}>
                      {account.code} · {account.name}
                    </option>
                  ))}
              </select>
            </label>
          ))}
        </div>
      </div>
    </section>
  );
}

function CompactForm({ children, onSubmit, testid, title }) {
  return (
    <form
      className="grid gap-3 border bg-card p-5 shadow-sm"
      data-testid={testid}
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <h2 className="font-display text-lg font-bold">{title}</h2>
      {children}
    </form>
  );
}
function AccountSelect({ accounts, id, label, onChange, value }) {
  return (
    <label className="grid gap-1 text-sm">
      <span>{label}</span>
      <select
        className="border bg-background px-3 py-2"
        data-testid={id}
        onChange={(event) => onChange(event.target.value)}
        required
        value={value}
      >
        <option value="">Select account</option>
        {accounts.map((account) => (
          <option key={account.account_id} value={account.account_id}>
            {account.code} · {account.name}
          </option>
        ))}
      </select>
    </label>
  );
}
function BasicSelect({ id, items, label, onChange, value }) {
  return (
    <label className="grid gap-1 text-sm">
      <span>{label}</span>
      <select
        className="border bg-background px-3 py-2"
        data-testid={id}
        onChange={(event) => onChange(event.target.value)}
        required
        value={value}
      >
        <option value="">Select</option>
        {items.map((item) => (
          <option
            key={item.supplier_id || item.purchase_id}
            value={item.supplier_id || item.purchase_id}
          >
            {item.name || `${item.supplier_name} · ${inr(item.outstanding)}`}
          </option>
        ))}
      </select>
    </label>
  );
}
function VoucherTable({ onReverse, vouchers }) {
  return (
    <div
      className="overflow-x-auto border bg-card"
      data-testid="accounting-vouchers-table"
    >
      <table className="min-w-[900px] w-full text-sm">
        <thead className="bg-muted/60">
          <tr>
            <th className="p-3 text-left">Voucher</th>
            <th className="p-3 text-left">Date</th>
            <th className="p-3 text-left">Narration</th>
            <th className="p-3 text-left">Source</th>
            <th className="p-3 text-right">Total</th>
            <th className="p-3 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {vouchers.length === 0 ? (
            <tr>
              <td className="p-8 text-center text-muted-foreground" colSpan={6}>
                No vouchers posted.
              </td>
            </tr>
          ) : (
            vouchers.map((voucher) => (
              <tr
                className="border-t"
                data-testid={`accounting-voucher-${voucher.voucher_id}`}
                key={voucher.voucher_id}
              >
                <td className="p-3 font-mono text-xs">
                  {voucher.voucher_number}
                </td>
                <td className="p-3">{voucher.date}</td>
                <td className="p-3">{voucher.narration}</td>
                <td className="p-3">
                  {voucher.source_type || voucher.voucher_type}
                </td>
                <td className="p-3 text-right">{inr(voucher.total)}</td>
                <td className="p-3 text-right">
                  {!voucher.reversal_status && (
                    <Button
                      data-testid={`accounting-reverse-voucher-${voucher.voucher_id}`}
                      onClick={() => onReverse(voucher)}
                      size="icon"
                      title="Reverse voucher"
                      variant="ghost"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
function ReportTable({ rows, title, totals }) {
  return (
    <div
      className="overflow-x-auto border bg-card p-5"
      data-testid="accounting-trial-balance-report"
    >
      <h2 className="font-display text-xl font-bold">{title}</h2>
      <table className="mt-4 min-w-[620px] w-full text-sm">
        <thead className="bg-muted/60">
          <tr>
            <th className="p-3 text-left">Code</th>
            <th className="p-3 text-left">Account</th>
            <th className="p-3 text-right">Debit</th>
            <th className="p-3 text-right">Credit</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr className="border-t" key={row.account_id}>
              <td className="p-3">{row.code}</td>
              <td className="p-3">{row.name}</td>
              <td className="p-3 text-right">
                {row.debit ? inr(row.debit) : "—"}
              </td>
              <td className="p-3 text-right">
                {row.credit ? inr(row.credit) : "—"}
              </td>
            </tr>
          ))}
          <tr className="border-t-2 font-bold">
            <td className="p-3" colSpan={2}>
              Total
            </td>
            <td className="p-3 text-right">{inr(totals[0])}</td>
            <td className="p-3 text-right">{inr(totals[1])}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
function Statement({ footer, left, leftTitle, right, rightTitle, title }) {
  return (
    <div
      className="border bg-card p-5"
      data-testid={`accounting-${title.toLowerCase().replaceAll(" ", "-")}-statement`}
    >
      <h2 className="font-display text-xl font-bold">{title}</h2>
      <div className="mt-4 grid gap-6 sm:grid-cols-2">
        <StatementList rows={left} title={leftTitle} />
        <StatementList rows={right} title={rightTitle} />
      </div>
      <p className="mt-5 border-t pt-3 font-semibold">{footer}</p>
    </div>
  );
}
function StatementList({ rows, title }) {
  return (
    <div>
      <h3 className="font-medium">{title}</h3>
      {rows.map((row) => (
        <div
          className="mt-2 flex justify-between gap-3 border-b pb-2 text-sm"
          key={row.name}
        >
          <span>{row.name}</span>
          <span>{inr(row.amount)}</span>
        </div>
      ))}
    </div>
  );
}
function Metric({ label, value }) {
  return (
    <div>
      <p className="text-muted-foreground">{label}</p>
      <p className="font-display text-lg font-bold">{value}</p>
    </div>
  );
}
